/**
 * SearchAnalyticsPanel — "Top searches" admin view.
 *
 * Surfaces anonymous site-search demand: most-searched terms and (most
 * valuable) zero-result terms that signal unmet content demand.
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, TrendingUp, AlertCircle, RefreshCw } from "lucide-react";
import api from "../../lib/api";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return ""; }
};

const SearchAnalyticsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/search-analytics");
      setData(data);
    } catch {
      toast.error("Couldn't load search analytics.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="search-analytics-panel">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <Search className="text-[#7737F6] mt-1" size={20} />
          <div>
            <h2 className="ek-heading text-2xl ek-text">Top searches</h2>
            <p className="ek-body text-[14px] mt-1 max-w-2xl">
              What visitors search for across the site. Zero-result terms are the highest-signal
              prompts for new insights or service copy — they're demand you aren't yet meeting.
            </p>
            {data && (
              <p className="text-[12px] ek-text-muted mt-2" data-testid="search-analytics-total">
                {data.total_searches} searches logged.
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          className="ek-btn ek-btn-outline-dark text-[12px] px-4 py-2 inline-flex items-center gap-1.5 shrink-0"
          data-testid="search-analytics-refresh"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading ? (
        <p className="ek-text-muted text-sm" data-testid="search-analytics-loading">Loading…</p>
      ) : !data || data.total_searches === 0 ? (
        <div className="ek-surface p-10 text-center border ek-line" data-testid="search-analytics-empty">
          <h3 className="ek-heading text-xl ek-text">No searches yet.</h3>
          <p className="ek-body text-[14px] mt-3 max-w-md mx-auto">
            Once visitors use the site search (the magnifier in the header or ⌘K), their queries
            appear here so you can see what people are looking for.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <div className="ek-eyebrow mb-3 inline-flex items-center gap-1.5"><TrendingUp size={12} /> Most searched</div>
            <div className="border ek-line divide-y divide-zinc-100" data-testid="search-analytics-top">
              {data.top.length === 0 ? (
                <p className="p-4 text-[13px] ek-text-muted">No data.</p>
              ) : data.top.map((t) => (
                <div key={t.query} className="flex items-center justify-between gap-4 p-3" data-testid="search-analytics-top-row">
                  <div className="min-w-0">
                    <div className="text-[14px] ek-text truncate">{t.query}</div>
                    <div className="text-[11.5px] ek-text-muted">avg {t.avg_results} result{t.avg_results === 1 ? "" : "s"} · {fmtDate(t.last_seen)}</div>
                  </div>
                  <span className="text-[12px] font-semibold text-[#7737F6] shrink-0">{t.count}×</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="ek-eyebrow mb-3 inline-flex items-center gap-1.5 text-amber-600"><AlertCircle size={12} /> Zero-result searches</div>
            <div className="border border-amber-200 divide-y divide-amber-100 bg-amber-50/40" data-testid="search-analytics-zero">
              {data.zero_result.length === 0 ? (
                <p className="p-4 text-[13px] ek-text-muted">None — every search returned something. 🎯</p>
              ) : data.zero_result.map((z) => (
                <div key={z.query} className="flex items-center justify-between gap-4 p-3" data-testid="search-analytics-zero-row">
                  <div className="min-w-0">
                    <div className="text-[14px] ek-text truncate">{z.query}</div>
                    <div className="text-[11.5px] ek-text-muted">last {fmtDate(z.last_seen)}</div>
                  </div>
                  <span className="text-[12px] font-semibold text-amber-600 shrink-0">{z.count}×</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchAnalyticsPanel;
